import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Image, PlayerListElement } from "../../../../../client/api";
import PlayerImage from "../../../PlayerImage";
import StoreMiniBanner from "../../../StoreMiniBanner";
import { translate } from "../../../../../config/translator";
import { ClavaContext } from "../../../../../config/contexts";
import { pathPlayerView } from "../../../../../config/routes";
import ScaleFade from "../../../Animated/ScaleFade";

type PlayerCardProps = {
  player?: PlayerListElement | null;
  emblem?: Image;
  goals?: number;
  position: "left" | "right";
  isClicked: boolean;
  onClick: () => void;
  onCloseBanner: () => void;
};

const PlayerCard: React.FC<PlayerCardProps> = ({
  player,
  emblem,
  goals,
  position,
  isClicked,
  onClick,
  onCloseBanner,
}) => {
  const { l } = useContext(ClavaContext);
  const navigate = useNavigate();
  const isLeft = position === "left";

  const handlePlayerClick = () => {
    if (player?.id) {
      navigate(pathPlayerView(player.id));
    } else {
      onClick();
    }
  };

  const renderAvatar = () => (
    <div className={`relative inline-block flex-none ${isLeft ? "mr-2" : "ml-2"}`}>
      {player ? (
        <ScaleFade isOpen hoverOnContainer className="w-16 h-16">
          <button
            type="button"
            onClick={handlePlayerClick}
            className="w-16 h-16 overflow-hidden rounded-full cursor-pointer"
          >
            <PlayerImage
              image={player.photo ?? undefined}
              width={64}
              className="rounded-full"
              round
            />
          </button>
        </ScaleFade>
      ) : (
        <div className="w-16 h-16 rounded-full bg-bg-100 dark:bg-bg-100-dark" />
      )}
      {emblem && player && (
        <div className={`absolute -bottom-1 z-10 ${isLeft ? "-right-1" : "-left-1"}`}>
          <PlayerImage image={emblem} width={24} className="rounded-full" round />
        </div>
      )}
      {isClicked && player && (
        <div className={`absolute -top-2 z-10 ${isLeft ? "left-8" : "right-8"}`}>
          <StoreMiniBanner
            compact
            noBorder
            message={translate("downloadUnlimited", l)}
            onClose={onCloseBanner}
            autoHideMs={6000}
          />
        </div>
      )}
    </div>
  );

  const renderPlayerInfo = () => (
    <div className={`w-full min-w-0 ${!isLeft ? "text-right" : ""} ${!player && !isLeft ? "flex flex-col items-end" : ""}`}>
      {player ? (
        <>
          <span className="block w-full font-semibold text-font dark:text-font-dark overflow-hidden text-ellipsis whitespace-nowrap text-xs">
            {player.familyName}
          </span>
          <span className="block w-full font-normal text-font-600 dark:text-font-600-dark overflow-hidden text-ellipsis whitespace-nowrap text-xs">
            {player.givenName}
          </span>
          <span className="font-semibold text-font dark:text-font-dark text-xs">
            {goals}
          </span>
        </>
      ) : (
        <>
          <span className={`block w-full h-5 bg-bg-100 dark:bg-bg-100-dark rounded-full ${!isLeft ? "ml-auto" : ""}`} />
          <span className={`block w-20 h-4 mt-0.5 bg-bg-100 dark:bg-bg-100-dark rounded-full ${!isLeft ? "ml-auto" : ""}`} />
          <span className={`block w-8 h-5 mt-0.5 bg-bg-100 dark:bg-bg-100-dark rounded-full ${!isLeft ? "ml-auto" : ""}`} />
        </>
      )}
    </div>
  );

  return (
    <div className="relative flex flex-row items-center gap-0 min-w-0 sm:flex-1">
      {!player && (
        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
          <div className="px-3 py-2 bg-bg dark:bg-bg-dark border border-light-gray dark:border-light-gray-dark text-font-600 dark:text-font-600-dark rounded-full text-xs text-center whitespace-normal break-words">
            {translate("squadNotAvailable", l)}
          </div>
        </div>
      )}
      {isLeft ? (
        <>
          {renderAvatar()}
          {renderPlayerInfo()}
        </>
      ) : (
        <>
          {renderPlayerInfo()}
          {renderAvatar()}
        </>
      )}
    </div>
  );
};

export default PlayerCard;



